import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import food from "../assets/images/order/food.png";
import add from "../assets/images/restaurants/add.png";
import minus from "../assets/images/restaurants/minus.png";
import pink_plus from "../assets/images/order/pink_plus.png";
import arrow from "../assets/images/order/orange-arrow.png";
import CouponList from "../components/CouponList";
import FoodTypeIcon from "../components/FoodTypeIcon";
import PaymentMode from "../modal/PaymentMode";
import AddAddressModal, { AddressData } from "../modal/AddAddressModal";
import LocationPickerModal from "../modal/LocationPickerModal";
import { useCart } from "../context/CartContext";
import {
  restaurantService,
  addressBookService,
  AddressBookData,
  orderService,
  PricingResponse,
  CouponData,
} from "../services";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";
import {
  getPendingCoupon,
  persistAddressDeliveryInstructions,
  getAddressDeliveryInstructions,
  joinDeliveryInstructions,
} from "../utils/helpers";

interface OrderItem {
  id: string | number;
  name: string;
  quantity: number;
  price: number;
  originalPrice?: number;
  image: string;
  dishId: string;
  restaurantId: string;
  foodType?: string | { name?: string } | null;
}

const OrderSummary = () => {
  const navigate = useNavigate();
  const { cartItems, updateCartItem, removeFromCart } = useCart();
  const [restaurant, setRestaurant] = useState<any>(null);
  const [isCouponListopen, setIsCouponListOpen] = useState<boolean>(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState<boolean>(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState<boolean>(false);
  const [selectedLocationData, setSelectedLocationData] = useState<any>(null);
  const [selectedAddress, setSelectedAddress] = useState<AddressBookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState<PricingResponse["data"] | null>(null);
  const [coupons, setCoupons] = useState<CouponData[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [selectedPayment] = useState("cod");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [cookingInstructions, setCookingInstructions] = useState("");

  const restaurantId = cartItems.length > 0 ? cartItems[0].restaurantId : null;

  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId) {
        setLoading(false);
        return;
      }

      try {
        const response: any =
          await restaurantService.getSingleRestaurant(restaurantId);
        setRestaurant(response.data || null);

        const selectedAddressId = localStorage.getItem("selectedDeliveryAddressId");
        const addressResponse: any = await addressBookService.getAllAddresses();
        if (addressResponse.status && addressResponse.data) {
          if (selectedAddressId) {
            const address = addressResponse.data.find(
              (addr: AddressBookData) => addr._id === selectedAddressId
            );
            setSelectedAddress(address || addressResponse.data.find((a: AddressBookData) => a.isDefault) || addressResponse.data[0] || null);
          } else {
            const defaultAddress = addressResponse.data.find(
              (addr: AddressBookData) => addr.isDefault
            );
            setSelectedAddress(defaultAddress || addressResponse.data[0] || null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [restaurantId]);

  useEffect(() => {
    const ids = [restaurantId, restaurant?._id, restaurant?.restaurantId].filter(
      Boolean
    ) as string[];
    for (const id of ids) {
      const pending = getPendingCoupon<CouponData>(id);
      if (pending?.couponCode) {
        setAppliedCoupon(pending);
        break;
      }
    }
  }, [restaurantId, restaurant?._id, restaurant?.restaurantId]);

  useEffect(() => {
    if (!selectedAddress?._id) return;
    const saved = getAddressDeliveryInstructions(selectedAddress._id);
    if (saved.length > 0 && !deliveryInstructions) {
      setDeliveryInstructions(joinDeliveryInstructions(saved));
    }
  }, [selectedAddress?._id, deliveryInstructions]);

  const items: OrderItem[] = useMemo(() => {
    return cartItems.map((cartItem: any) => {
      const dish = cartItem.dish;
      return {
        id: cartItem._id,
        dishId: cartItem.dishId,
        restaurantId: cartItem.restaurantId,
        name: dish?.dishName || "Item",
        quantity: cartItem.quantity,
        price: dish?.basePrice || 0,
        originalPrice: dish?.comparePrice || dish?.basePrice || 0,
        image: dish?.images?.[0]?.url || food,
        foodType: dish?.foodType,
      };
    });
  }, [cartItems]);

  const subtotalFallback = items.reduce(
    (sum: number, item: OrderItem) => sum + item.price * item.quantity,
    0
  );

  useEffect(() => {
    if (!restaurantId || cartItems.length === 0) return;

    const fetchPricing = async () => {
      try {
        const pricingPayload = {
          restaurantId,
          items: cartItems.map((item: any) => ({
            dishId: item.dishId,
            variantId: item.variantId || null,
            addons: item.addons || [],
            quantity: item.quantity,
          })),
          paymentMethod: selectedPayment,
          couponCode: appliedCoupon?.couponCode,
          ...(selectedAddress
            ? {
                deliveryAddress: {
                  fullName: selectedAddress.fullName,
                  phone: selectedAddress.phone,
                  addressLine1: selectedAddress.addressLine1,
                  addressLine2: selectedAddress.addressLine2 || "",
                  city: selectedAddress.city,
                  pincode: selectedAddress.pincode,
                },
              }
            : {}),
        };

        const response: any = await orderService.getPricing(pricingPayload);
        if (response.status && response.data) {
          setPricingData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch pricing:", error);
      }
    };

    fetchPricing();
  }, [restaurantId, cartItems, selectedAddress, selectedPayment, appliedCoupon]);

  useEffect(() => {
    if (!restaurantId || cartItems.length === 0) return;
    const orderValue = subtotalFallback;
    if (orderValue === 0) return;

    const fetchCoupons = async () => {
      setCouponsLoading(true);
      try {
        const res: any = await orderService.getApplicableCoupons(
          restaurantId,
          orderValue
        );
        if (res.status && res.data) {
          setCoupons(res.data);
        }
      } catch (error) {
        console.error("Error fetching coupons:", error);
      } finally {
        setCouponsLoading(false);
      }
    };
    fetchCoupons();
  }, [restaurantId, cartItems, subtotalFallback]);

  const handleIncrement = async (item: OrderItem) => {
    try {
      await updateCartItem(item.dishId, item.restaurantId, item.quantity + 1);
    } catch (error) {
      console.error("Failed to increment item:", error);
    }
  };

  const handleDecrement = async (item: OrderItem) => {
    try {
      if (item.quantity - 1 === 0) {
        await removeFromCart(item.id.toString());
      } else {
        await updateCartItem(item.dishId, item.restaurantId, item.quantity - 1);
      }
    } catch (error) {
      console.error("Failed to decrement item:", error);
    }
  };

  const handleLocationSelect = (locationData: any) => {
    setSelectedLocationData(locationData);
    setIsLocationPickerOpen(false);
    setIsAddAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData: AddressData) => {
    try {
      const createData = {
        fullName: addressData.fullName,
        phone: addressData.phone,
        addressLine1: addressData.addressLine1,
        addressLine2: addressData.addressLine2,
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pincode,
        isDefault: true,
        geoLocation: addressData.geoLocation,
        deliveryInstructions: joinDeliveryInstructions(
          addressData.deliveryInstructions
        ),
      };
      const response: any = await addressBookService.createAddress(createData);
      if (response.status && response.data) {
        persistAddressDeliveryInstructions(
          response.data._id,
          addressData.deliveryInstructions
        );
        setSelectedAddress(response.data);
        localStorage.setItem("selectedDeliveryAddressId", response.data._id);
        if (addressData.deliveryInstructions.length > 0) {
          setDeliveryInstructions(
            joinDeliveryInstructions(addressData.deliveryInstructions)
          );
        }
        toast.success("Address saved");
        setIsAddAddressModalOpen(false);
        setSelectedLocationData(null);
      } else {
        toast.error(response.message || "Failed to save address");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save address");
    }
  };

  const handleApplyCouponByCode = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (!restaurantId) {
      setCouponError("Restaurant not found for coupon validation");
      return;
    }

    const match = coupons.find((c) => c.couponCode.toUpperCase() === code);
    if (match && subtotalFallback < match.minOrderValue) {
      setCouponError(`Min. order value ₹${match.minOrderValue} required`);
      return;
    }

    try {
      const res: any = await orderService.validateCoupon({
        couponCode: code,
        restaurantId,
        orderValue: subtotalFallback,
      });

      if (res?.status === false || res?.data?.isValid === false) {
        setCouponError(res?.message || res?.data?.message || "Invalid coupon");
        return;
      }

      const couponFromApi = res?.data?.coupon || res?.data;
      setAppliedCoupon({
        _id: couponFromApi?._id || match?._id || `validated-${code}`,
        couponCode: couponFromApi?.couponCode || code,
        description:
          couponFromApi?.description || match?.description || "Applied coupon",
        discountType: couponFromApi?.discountType || match?.discountType || "flat",
        discountValue:
          couponFromApi?.discountValue ??
          res?.data?.discountAmount ??
          match?.discountValue ??
          0,
        maxDiscountCap:
          couponFromApi?.maxDiscountCap ?? match?.maxDiscountCap ?? 0,
        minOrderValue:
          couponFromApi?.minOrderValue ?? match?.minOrderValue ?? 0,
        isActive: true,
      });
      setCouponInput("");
      setCouponError("");
      toast.success(res?.message || "Coupon applied");
    } catch (error: any) {
      // Fallback: allow known applicable coupon locally if validate endpoint fails
      if (match) {
        setAppliedCoupon(match);
        setCouponInput("");
        setCouponError("");
        toast.success("Coupon applied");
        return;
      }
      setCouponError(error?.message || "Invalid coupon code");
    }
  };

  const handleApplyCoupon = async (coupon: CouponData) => {
    if (!restaurantId) {
      setAppliedCoupon(coupon);
      setCouponInput("");
      setCouponError("");
      return;
    }
    try {
      const res: any = await orderService.validateCoupon({
        couponCode: coupon.couponCode,
        restaurantId,
        orderValue: subtotalFallback,
      });
      if (res?.status === false || res?.data?.isValid === false) {
        toast.error(res?.message || "Coupon not valid for this order");
        return;
      }
      setAppliedCoupon(coupon);
      setCouponInput("");
      setCouponError("");
      toast.success("Coupon applied");
    } catch {
      setAppliedCoupon(coupon);
      setCouponInput("");
      setCouponError("");
      toast.success("Coupon applied");
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError("");
  };

  const handleCheckout = () => {
    if (!selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const subtotal = pricingData?.subtotal || subtotalFallback;
  const deliveryCharges = pricingData?.deliveryFee || 0;
  const platformFee = pricingData?.taxAmount || 0;
  const couponDiscount = pricingData?.couponDiscount ?? 0;
  const total =
    pricingData?.totalAmount ||
    Math.max(subtotal + deliveryCharges + platformFee - couponDiscount, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-lg dark:text-white">Loading...</div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white dark:bg-gray-900">
        <div className="text-xl font-semibold dark:text-white">
          Your cart is empty
        </div>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Browse Restaurants
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#FFFFFF] dark:bg-gray-900 p-2 md:p-[32px] border-t-2 dark:border-gray-700 min-h-[calc(100vh-7.5rem)] md:min-h-screen transition-colors duration-300 mt-[7.5rem] md:mt-0">
      <h6 className="text-[#050505] dark:text-white transition-colors">
        Search / {restaurant?.businessName || "Restaurant"} / Order Summary
      </h6>

      <div className="flex flex-col md:flex-row md:h-[90%]">
        <div className="w-full md:w-[35%] p-1 md:p-6 flex flex-col justify-between">
          <div className="rounded-[15px] border-2 dark:border-gray-700 bg-white dark:bg-gray-800 p-[15px] transition-colors duration-300">
            <h6 className="pb-[14px] border-b-2 dark:border-gray-700 text-[#050505] dark:text-white">
              Order Items {items.length}
            </h6>
            <div className="pb-[15px] border-b-2 dark:border-gray-700">
              {items.map((item: OrderItem) => (
                <div
                  key={item.id}
                  className="cursor-pointer transition-colors flex justify-between items-center my-[15px]"
                >
                  <div className="flex justify-center items-center gap-[10px]">
                    <img
                      src={item.image}
                      alt="Food Item"
                      className="h-[75px] w-[75px] rounded-[12px]"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <FoodTypeIcon foodType={item.foodType} size={14} />
                        <h6 className="text-[#050505] dark:text-white text-left font-urbanist text-[17px] font-medium leading-[24px]">
                          {item.name}
                        </h6>
                      </div>
                      <p className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[17px] font-normal leading-[21px] tracking-[-0.332px]">
                        {item.price} x {item.quantity}
                        <span className="text-[#7402A7] dark:text-purple-400 font-urbanist font-medium ml-[10px]">
                          {item.price * item.quantity}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="bg-[#F5F5F5] dark:bg-gray-700 text-[#7402A7] dark:text-purple-400 rounded-[8px] font-urbanist font-semibold text-[18px] w-[38px] h-[38px] flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => handleDecrement(item)}
                    >
                      <img src={minus} alt="minus" className="h-[20px] w-[20px] dark:invert" />
                    </div>
                    <span className="text-white bg-[#7402A7] dark:bg-purple-600 font-urbanist text-[18px] font-semibold text-center rounded-[10px] px-[15px] py-[6px]">
                      {item.quantity}
                    </span>
                    <div
                      className="bg-[#F5F5F5] dark:bg-gray-700 text-[#7402A7] dark:text-purple-400 rounded-[8px] font-urbanist font-semibold text-[18px] w-[38px] h-[38px] flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => handleIncrement(item)}
                    >
                      <img src={add} alt="add" className="h-[20px] w-[20px] dark:invert" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex items-center gap-[10px] mt-[15px] cursor-pointer p-[15px] rounded-[9px] justify-center bg-[#F5F5F5] dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={() => navigate(`/restaurants/${restaurantId}`)}
            >
              <img src={pink_plus} alt="Pink Plus" className="h-[19px] w-[19px] dark:invert" />
              <p className="text-[#7402A7] dark:text-purple-400 font-medium">
                Add More Items
              </p>
            </div>
          </div>

          <button
            onClick={handleCheckout}
            className="w-full hidden md:flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors"
          >
            <span className="text-white font-urbanist text-[20px] font-semibold leading-[140%]">
              Checkout
            </span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img src={arrow} alt="right arrow" className="h-[41px] w-[30px]" />
            </div>
          </button>
        </div>

        <div className="w-full md:w-[65%]">
          <div className="border-2 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[19px] p-[15px] mt-[22px] flex flex-col gap-[15px] transition-colors duration-300">
            <div className="flex justify-between items-center pb-[15px] border-b-2 dark:border-gray-700">
              <p className="text-[#050505] dark:text-white font-urbanist text-[19px] font-semibold leading-[140%]">
                Coupon Code
              </p>
              <button
                className="text-[#7402A7] dark:text-purple-400 font-urbanist text-[15px] font-semibold leading-[18px]"
                onClick={() => setIsCouponListOpen(true)}
              >
                {couponsLoading
                  ? "Loading..."
                  : `${coupons.length} Available Coupons`}
              </button>
            </div>

            {appliedCoupon ? (
              <div className="flex justify-between items-center px-[15px] py-[10px] rounded-[15px] bg-[#F5F5F5] dark:bg-gray-700">
                <div>
                  <p className="text-[#050505] dark:text-white font-urbanist font-semibold">
                    {appliedCoupon.couponCode}
                  </p>
                  <p className="text-[#6A6A6A] dark:text-gray-400 text-sm">
                    {appliedCoupon.description}
                  </p>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-red-500 font-urbanist text-[15px] font-semibold"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center px-[15px] py-[10px] rounded-[15px] bg-[#F5F5F5] dark:bg-gray-700">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value.toUpperCase());
                    setCouponError("");
                  }}
                  placeholder="Enter coupon code"
                  className="text-[#050505] dark:text-white dark:placeholder-gray-400 font-urbanist text-[17px] leading-[160%] w-full bg-transparent outline-none pr-[15px]"
                />
                <button
                  onClick={handleApplyCouponByCode}
                  className="text-white font-urbanist text-[15px] font-semibold leading-[18px] px-[15px] py-[9px] rounded-[9px] border-2 border-[#7402A7] dark:border-purple-600 bg-[#7402A7] dark:bg-purple-600 hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            )}
            {couponError && (
              <p className="text-red-500 text-sm font-urbanist">{couponError}</p>
            )}
          </div>

          <div className="border-2 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[19px] p-[15px] mt-[22px] flex flex-col gap-[15px] transition-colors duration-300">
            <p className="text-[#050505] dark:text-white font-urbanist text-[19px] font-semibold leading-[140%]">
              Instructions
            </p>
            <div>
              <label className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mb-1 block">
                Delivery instructions
              </label>
              <input
                type="text"
                value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)}
                placeholder="e.g. Leave at reception, call on arrival"
                className="w-full px-[15px] py-[10px] rounded-[15px] bg-[#F5F5F5] dark:bg-gray-700 text-[#050505] dark:text-white font-urbanist outline-none"
              />
            </div>
            <div>
              <label className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mb-1 block">
                Cooking requests
              </label>
              <input
                type="text"
                value={cookingInstructions}
                onChange={(e) => setCookingInstructions(e.target.value)}
                placeholder="e.g. Less spicy, no onion"
                className="w-full px-[15px] py-[10px] rounded-[15px] bg-[#F5F5F5] dark:bg-gray-700 text-[#050505] dark:text-white font-urbanist outline-none"
              />
            </div>
          </div>

          <div className="border-2 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[19px] mt-[22px] transition-colors duration-300">
            <div className="p-[15px] flex flex-col gap-[15px]">
              <div className="pb-[15px] border-b-2 dark:border-gray-700">
                <p className="text-[#050505] dark:text-white font-urbanist text-[19px] font-semibold leading-[140%]">
                  Charges & GST
                </p>
              </div>
              <div className="py-[15px] border-b-2 dark:border-gray-700 space-y-[10px]">
                <div className="flex justify-between items-center">
                  <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[17px] font-medium leading-[160%]">
                    Subtotal
                  </p>
                  <span className="text-[#050505] dark:text-white">
                    ₹ {subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[17px] font-medium leading-[160%]">
                    Delivery Charges
                  </p>
                  <span className="text-[#050505] dark:text-white">
                    ₹ {deliveryCharges.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[17px] font-medium leading-[160%]">
                    Platform Fee
                  </p>
                  <span className="text-[#050505] dark:text-white">
                    ₹ {platformFee.toFixed(2)}
                  </span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between items-center">
                    <p className="text-[#050505] dark:text-gray-300 font-urbanist text-[17px] font-medium leading-[160%]">
                      Coupon
                    </p>
                    <span className="text-[#02A72B] dark:text-green-400">
                      - ₹ {couponDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center">
                <p className="text-[#050505] dark:text-white font-urbanist text-[19px] font-medium leading-[160%]">
                  Total
                </p>
                <span className="text-[#050505] dark:text-white font-urbanist text-[19px] font-semibold leading-[160%]">
                  ₹ {total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="border-2 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[19px] mt-[22px] transition-colors duration-300">
            <div className="p-[15px] flex flex-col gap-[15px]">
              <div className="pb-[15px] border-b-2 dark:border-gray-700">
                <p className="text-[#050505] dark:text-white font-urbanist text-[19px] font-semibold leading-[140%]">
                  Address
                </p>
              </div>

              {selectedAddress ? (
                <div className="flex justify-between items-start gap-4 bg-[#F5F5F5] dark:bg-gray-700 p-4 rounded-[15px]">
                  <div className="flex-1">
                    <h6 className="text-[#050505] dark:text-white font-urbanist text-[17px] font-semibold leading-[24px] mb-1">
                      {selectedAddress.fullName}
                    </h6>
                    <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[15px] leading-[21px]">
                      {selectedAddress.addressLine1}
                      {selectedAddress.addressLine2 &&
                        `, ${selectedAddress.addressLine2}`}
                    </p>
                    <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[15px] leading-[21px]">
                      {selectedAddress.city}, {selectedAddress.state} -{" "}
                      {selectedAddress.pincode}
                    </p>
                    <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] leading-[21px] mt-1">
                      {selectedAddress.phone}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      navigate("/saved-addresses", {
                        state: { from: "order-summary" },
                      })
                    }
                    className="px-4 py-2 border-2 border-[#7402A7] dark:border-purple-600 text-[#7402A7] dark:text-purple-400 rounded-[10px] font-urbanist text-[15px] font-semibold hover:bg-purple-50 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="flex justify-center items-center gap-3 flex-col">
                  <button
                    onClick={() => setIsLocationPickerOpen(true)}
                    className="border-2 border-dotted rounded-[15px] w-[90%] mx-auto p-4 text-xl text-[#7402A7] dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    + Add Delivery Address
                  </button>
                  <button
                    onClick={() =>
                      navigate("/saved-addresses", {
                        state: { from: "order-summary" },
                      })
                    }
                    className="text-[#7402A7] dark:text-purple-400 font-urbanist text-sm"
                  >
                    Choose from saved addresses
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleCheckout}
            className="w-full flex md:hidden items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors mt-4"
          >
            <span className="text-white font-urbanist text-[20px] font-semibold leading-[140%]">
              Checkout
            </span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img src={arrow} alt="right arrow" className="h-[41px] w-[30px]" />
            </div>
          </button>
        </div>
      </div>

      {isCouponListopen && (
        <CouponList
          onClose={() => setIsCouponListOpen(false)}
          coupons={coupons}
          loading={couponsLoading}
          subtotal={subtotalFallback}
          onApply={handleApplyCoupon}
          appliedCouponCode={appliedCoupon?.couponCode}
        />
      )}
      {isPaymentModalOpen && (
        <PaymentMode
          onClose={() => setIsPaymentModalOpen(false)}
          total={total}
          restaurantId={restaurantId || ""}
          cartItems={cartItems}
          couponCode={appliedCoupon?.couponCode}
          deliveryInstructions={deliveryInstructions}
          cookingInstructions={cookingInstructions}
          deliveryAddress={
            selectedAddress
              ? {
                  fullName: selectedAddress.fullName,
                  phone: selectedAddress.phone,
                  addressLine1: selectedAddress.addressLine1,
                  addressLine2: selectedAddress.addressLine2,
                  city: selectedAddress.city,
                  pincode: selectedAddress.pincode,
                  geoLocation: selectedAddress.geoLocation,
                }
              : undefined
          }
        />
      )}
      {isLocationPickerOpen && (
        <LocationPickerModal
          isOpen={isLocationPickerOpen}
          onClose={() => setIsLocationPickerOpen(false)}
          onLocationSelect={handleLocationSelect}
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        />
      )}
      {isAddAddressModalOpen && (
        <AddAddressModal
          isOpen={isAddAddressModalOpen}
          onClose={() => setIsAddAddressModalOpen(false)}
          onSave={handleSaveAddress}
          initialLocationData={selectedLocationData}
        />
      )}
    </div>
  );
};

export default OrderSummary;
